import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import { n0, formatCost, formatDurationMs } from './usageFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './UsageAnalyticsPanel.module.scss';

const entityLabel = (entry, companyLabelFn) => {
  const isCompanyEntry =
    entry.company_id != null && !entry.model_label && !entry.user_name && !entry.user_email;
  if (isCompanyEntry && companyLabelFn) return companyLabelFn(entry.company_id, entry.company_name);
  return (
    entry.model_label ||
    entry.user_name ||
    entry.company_name ||
    entry.partner_name ||
    entry.agent_name ||
    entry.name ||
    entry.user_email ||
    '—'
  );
};

const entityValue = (entry) => Number(entry.total_tokens ?? entry.calls ?? entry.estimated_cost ?? 0);

const TopList = ({ title, entries, companyLabelFn }) => {
  const list = Array.isArray(entries) ? entries.slice(0, 5) : [];
  const max = Math.max(1, ...list.map(entityValue));

  return (
    <Box className={styles.topListCol}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No data for this period.
        </Typography>
      ) : (
        list.map((entry, idx) => {
          const value = entityValue(entry);
          const pct = Math.max(4, Math.round((value / max) * 100));
          return (
            <Box key={idx} className={styles.topRow}>
              <span className={styles.topRowLabel}>{entityLabel(entry, companyLabelFn)}</span>
              <Box className={styles.topBarTrack}>
                <Box className={styles.topBarFill} sx={{ width: `${pct}%` }} />
              </Box>
              <span className={styles.topRowValue}>{n0(value)}</span>
            </Box>
          );
        })
      )}
    </Box>
  );
};

/**
 * KPI cards + top-5 lists from `/usage/summary`. The second list is users for a
 * company admin and companies for the platform/partner leaderboards.
 */
const UsageSummaryCards = ({ summary, loading, topEntitiesTitle = 'Top users', companyLabelFn = null }) => {
  const totals = summary?.totals || {};
  const topModels = summary?.top_models || summary?.models || [];
  const topEntities = useMemo(
    () => summary?.top_users || summary?.top_companies || summary?.users || summary?.companies || [],
    [summary]
  );

  const cost = formatCost(totals.estimated_cost, totals.cost_coverage_pct);

  const tiles = [
    { label: 'Total tokens', value: n0(totals.total_tokens) },
    { label: 'Calls', value: n0(totals.calls) },
    { label: 'Est. cost', value: cost.text, tooltip: cost.tooltip },
    { label: 'Errors', value: n0(totals.errors) },
    { label: 'Avg duration', value: formatDurationMs(totals.avg_duration_ms) },
  ];

  if (loading) {
    return (
      <Box className={sharedStyles.tableCard} sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box className={sharedStyles.tableCard} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Box className={styles.kpiGrid}>
        {tiles.map((tile) => (
          <Box key={tile.label} className={styles.kpiTile}>
            <span className={styles.kpiLabel}>{tile.label}</span>
            {tile.tooltip ? (
              <Tooltip title={tile.tooltip}>
                <span className={styles.kpiValue}>{tile.value}</span>
              </Tooltip>
            ) : (
              <span className={styles.kpiValue}>{tile.value}</span>
            )}
          </Box>
        ))}
      </Box>

      <Box className={styles.topListsRow}>
        <TopList title="Top models" entries={topModels} />
        <TopList title={topEntitiesTitle} entries={topEntities} companyLabelFn={companyLabelFn} />
      </Box>
    </Box>
  );
};

export default UsageSummaryCards;
